import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Badge,
  Button,
  Card,
  EmptyState,
  FormField,
  Input,
  Modal,
  Select,
  Table,
  useToast,
  type Column,
} from "@/ui";
import { useT } from "@/app";
import { useCan } from "@/auth";
import { token } from "@/design/theme";
import {
  CAP,
  useAddEntree,
  useAutomates,
  useChargement,
  useChargementKey,
  useCloseChargement,
  useConfirmChargement,
  useCreateChargement,
} from "./api";
import type { ChargementEntree, ChargementPositionnel, ChargementStatut, Id } from "./types";
import { PageHeader } from "./components/PageHeader";
import { QueryBoundary } from "./components/QueryBoundary";
import { idStr } from "./lib";

const STATUT_TONE: Record<ChargementStatut, "info" | "success" | "neutral"> = {
  ouvert: "info",
  confirme: "success",
  clos: "neutral",
};

/**
 * ChargementPage — operator-confirmed positional load (LIS-9). Open a load (automate + référence),
 * add each rack position → tube, then SEAL it: only a `confirme` load binds a result to a position.
 * Sealing is irreversible and gated behind lis.chargement.confirm; the screen makes that explicit.
 */
export function ChargementPage() {
  const { t } = useT();
  const canCreate = useCan(CAP.chargementCreate);
  const [activeId, setActiveId] = useState<Id | null>(null);
  const [openCreate, setOpenCreate] = useState(false);

  return (
    <div>
      <PageHeader
        title={t("lis.chargement.title")}
        subtitle={t("lis.chargement.subtitle")}
        actions={
          canCreate ? (
            <Button variant="primary" onClick={() => setOpenCreate(true)}>
              {t("lis.chargement.open")}
            </Button>
          ) : undefined
        }
      />

      {activeId == null ? (
        <Picker onPick={setActiveId} canCreate={canCreate} onCreate={() => setOpenCreate(true)} />
      ) : (
        <ChargementDetail id={activeId} onBack={() => setActiveId(null)} />
      )}

      {openCreate && (
        <CreateChargementModal
          onClose={() => setOpenCreate(false)}
          onCreated={(id) => {
            setOpenCreate(false);
            setActiveId(id);
          }}
        />
      )}
    </div>
  );
}

// ── Picker: open by id (no list endpoint exists server-side) ──────────────────────
function Picker({
  onPick,
  canCreate,
  onCreate,
}: {
  onPick: (id: Id) => void;
  canCreate: boolean;
  onCreate: () => void;
}) {
  const { t } = useT();
  const [id, setId] = useState("");
  return (
    <Card title={t("lis.chargement.lookup")}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (id.trim()) onPick(id.trim());
        }}
        style={{ display: "flex", gap: token.s2, alignItems: "flex-end", flexWrap: "wrap" }}
      >
        <div style={{ flex: "1 1 200px" }}>
          <FormField label={t("lis.chargement.lookup.id")}>
            {(p) => (
              <Input
                {...p}
                mono
                inputMode="numeric"
                value={id}
                onChange={(e) => setId(e.target.value)}
                autoFocus
                style={{ inlineSize: "100%" }}
              />
            )}
          </FormField>
        </div>
        <Button type="submit" variant="secondary" disabled={!id.trim()}>
          {t("lis.action.confirm")}
        </Button>
      </form>
      {canCreate && (
        <p style={{ marginBlockEnd: 0, color: token.slate }}>
          <Button variant="ghost" size="sm" onClick={onCreate}>
            {t("lis.chargement.open")}
          </Button>
        </p>
      )}
    </Card>
  );
}

// ── Detail: positions + add + seal/close ──────────────────────────────────────────
function ChargementDetail({ id, onBack }: { id: Id; onBack: () => void }) {
  const { t } = useT();
  const q = useChargement(id);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: token.s4 }}>
      <Button variant="ghost" size="sm" onClick={onBack} style={{ alignSelf: "flex-start" }}>
        ‹ {t("lis.action.back")}
      </Button>
      <QueryBoundary q={q}>{(ch) => <ChargementBody ch={ch} />}</QueryBoundary>
    </div>
  );
}

function ChargementBody({ ch }: { ch: ChargementPositionnel }) {
  const { t } = useT();
  const toast = useToast();
  const qc = useQueryClient();
  const chargementKey = useChargementKey();

  const canUpdate = useCan(CAP.chargementUpdate);
  const canConfirm = useCan(CAP.chargementConfirm);

  const addEntree = useAddEntree();
  const confirmChargement = useConfirmChargement();
  const closeChargement = useCloseChargement();

  const [showAdd, setShowAdd] = useState(false);
  const [showSeal, setShowSeal] = useState(false);

  const sealed = ch.statut !== "ouvert";
  const invalidate = () => void qc.invalidateQueries({ queryKey: chargementKey(ch.id) });

  const seal = () => {
    confirmChargement.mutate(ch.id, {
      onSuccess: () => {
        toast.success(t("lis.chargement.confirmed"));
        invalidate();
        setShowSeal(false);
      },
      onError: (err) => toast.error(err.message, t("lis.chargement.confirmError")),
    });
  };

  const close = () => {
    closeChargement.mutate(ch.id, {
      onSuccess: () => {
        toast.success(t("lis.chargement.closed"));
        invalidate();
      },
      onError: (err) => toast.error(err.message, t("lis.chargement.closeError")),
    });
  };

  const columns: ReadonlyArray<Column<ChargementEntree>> = [
    {
      key: "position",
      header: t("lis.chargement.col.position"),
      mono: true,
      sortable: true,
      sortAccessor: (e) => e.position,
      cell: (e) => e.position,
    },
    {
      key: "tube",
      header: t("lis.chargement.col.tube"),
      mono: true,
      cell: (e) => idStr(e.tube_id),
    },
  ];

  return (
    <>
      <Card
        title={
          <span style={{ display: "flex", alignItems: "center", gap: token.s2, flexWrap: "wrap" }}>
            <span className="nv-mono nv-ltr-island">{ch.reference}</span>
            <Badge tone={STATUT_TONE[ch.statut]}>{t(`lis.chargement.statut.${ch.statut}`)}</Badge>
            <span style={{ color: token.slate, fontSize: token.fsLabel }}>
              {t("lis.chargement.expected", { n: ch.entrees.length })}
            </span>
          </span>
        }
        actions={
          !sealed && canUpdate ? (
            <Button variant="secondary" size="sm" onClick={() => setShowAdd(true)}>
              {t("lis.chargement.addPosition")}
            </Button>
          ) : undefined
        }
        padded={false}
      >
        {ch.entrees.length === 0 ? (
          <EmptyState
            glyph="▢"
            title={t("lis.chargement.empty.title")}
            description={t("lis.chargement.empty.desc")}
            {...(!sealed && canUpdate
              ? {
                  action: (
                    <Button variant="primary" onClick={() => setShowAdd(true)}>
                      {t("lis.chargement.addPosition")}
                    </Button>
                  ),
                }
              : {})}
          />
        ) : (
          <Table<ChargementEntree>
            columns={columns}
            rows={ch.entrees}
            rowKey={(e) => idStr(e.id)}
            caption={t("lis.chargement.positions")}
          />
        )}
      </Card>

      {sealed ? (
        ch.statut === "confirme" && (
          <p
            role="status"
            style={{
              margin: 0,
              padding: token.s3,
              borderRadius: token.rMd,
              background: token.tintSuccess,
              color: token.success,
              display: "flex",
              gap: token.s2,
              alignItems: "center",
            }}
          >
            <span aria-hidden>✓</span>
            {t("lis.chargement.sealedNote")}
            {canUpdate && (
              <Button
                variant="ghost"
                size="sm"
                onClick={close}
                loading={closeChargement.isPending}
                style={{ marginInlineStart: "auto" }}
              >
                {t("lis.chargement.close")}
              </Button>
            )}
          </p>
        )
      ) : (
        <div style={{ display: "flex", gap: token.s2, justifyContent: "flex-end" }}>
          {canConfirm && (
            <Button
              variant="primary"
              onClick={() => setShowSeal(true)}
              disabled={ch.entrees.length === 0}
            >
              {t("lis.chargement.confirm")}
            </Button>
          )}
        </div>
      )}

      {showAdd && (
        <AddPositionModal
          onClose={() => setShowAdd(false)}
          pending={addEntree.isPending}
          onAdd={(position, tube_id) =>
            addEntree.mutate(
              { chargementId: ch.id, position, tube_id },
              {
                onSuccess: () => {
                  toast.success(t("lis.chargement.added", { pos: position }));
                  invalidate();
                  setShowAdd(false);
                },
                onError: (err) => toast.error(err.message, t("lis.chargement.addError")),
              },
            )
          }
        />
      )}

      <Modal
        open={showSeal}
        onClose={() => setShowSeal(false)}
        title={t("lis.chargement.confirmTitle")}
        footer={
          <>
            <Button variant="ghost" onClick={() => setShowSeal(false)}>
              {t("lis.action.cancel")}
            </Button>
            <Button variant="primary" onClick={seal} loading={confirmChargement.isPending}>
              {t("lis.chargement.confirm")}
            </Button>
          </>
        }
      >
        <p style={{ margin: 0, display: "flex", gap: token.s2, alignItems: "flex-start" }}>
          <span aria-hidden style={{ color: token.warn }}>
            ▲
          </span>
          {t("lis.chargement.confirmWarn")}
        </p>
      </Modal>
    </>
  );
}

function AddPositionModal({
  onClose,
  onAdd,
  pending,
}: {
  onClose: () => void;
  onAdd: (position: string, tubeId: string) => void;
  pending: boolean;
}) {
  const { t } = useT();
  const [position, setPosition] = useState("");
  const [tube, setTube] = useState("");
  const valid = position.trim().length > 0 && tube.trim().length > 0;
  return (
    <Modal
      open
      onClose={onClose}
      title={t("lis.chargement.addPosition")}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("lis.action.cancel")}
          </Button>
          <Button
            variant="primary"
            onClick={() => valid && onAdd(position.trim(), tube.trim())}
            loading={pending}
            disabled={!valid}
          >
            {t("lis.action.confirm")}
          </Button>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (valid) onAdd(position.trim(), tube.trim());
        }}
        style={{ display: "flex", flexDirection: "column", gap: token.s3 }}
      >
        <FormField label={t("lis.chargement.field.position")} required>
          {(p) => (
            <Input
              {...p}
              mono
              value={position}
              onChange={(e) => setPosition(e.target.value)}
              autoFocus
              placeholder="A1"
            />
          )}
        </FormField>
        <FormField label={t("lis.chargement.field.tube")} required>
          {(p) => (
            <Input
              {...p}
              mono
              inputMode="numeric"
              value={tube}
              onChange={(e) => setTube(e.target.value)}
            />
          )}
        </FormField>
        {/* hidden submit so Enter submits the form */}
        <button type="submit" hidden aria-hidden tabIndex={-1} />
      </form>
    </Modal>
  );
}

function CreateChargementModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (id: Id) => void;
}) {
  const { t } = useT();
  const toast = useToast();
  const automatesQuery = useAutomates();
  const create = useCreateChargement();
  const [automateId, setAutomateId] = useState("");
  const [reference, setReference] = useState("");
  const valid = automateId.length > 0 && reference.trim().length > 0;

  const submit = () => {
    if (!valid) return;
    create.mutate(
      { automate_id: automateId, reference: reference.trim() },
      {
        onSuccess: (ch) => {
          toast.success(t("lis.chargement.created", { ref: reference.trim() }));
          onCreated(ch.id);
        },
        onError: (err) => toast.error(err.message, t("lis.chargement.createError")),
      },
    );
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={t("lis.chargement.openTitle")}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("lis.action.cancel")}
          </Button>
          <Button variant="primary" onClick={submit} loading={create.isPending} disabled={!valid}>
            {t("lis.chargement.open")}
          </Button>
        </>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: token.s3 }}>
        <QueryBoundary q={automatesQuery} loadingVariant="spinner">
          {(automates) => (
            <FormField label={t("lis.chargement.automate")} required>
              {(p) => (
                <Select
                  {...p}
                  value={automateId}
                  onChange={(e) => setAutomateId(e.target.value)}
                  placeholder={t("lis.chargement.automate")}
                  options={automates.map((a) => ({
                    value: idStr(a.id),
                    label: `${a.code} · ${a.libelle}`,
                  }))}
                />
              )}
            </FormField>
          )}
        </QueryBoundary>
        <FormField label={t("lis.chargement.reference")} required>
          {(p) => (
            <Input
              {...p}
              mono
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="RACK-001"
            />
          )}
        </FormField>
      </div>
    </Modal>
  );
}
